export interface AIProviderConfig {
  apiKey: string;
  baseUrl: string;
  model: string;
}

export interface GeneratedCharacter {
  id: string;
  name: string;
  age: string;
  occupation: string;
  personality: string;
  background: string;
  appearance: string;
  relationships: string[];
}

export interface EpisodeBeat {
  episode: number;
  title: string;
  summary: string;
  cliffhanger: string;
}

export interface ScriptScene {
  id: string;
  title: string;
  description: string;
  duration: string;
  location: string;
  characters: string[];
  shotType: string;
  cameraMovement: string;
  lighting: string;
  sound: string;
  dialogue: string;
  notes: string;
}

export interface GeneratedDramaProject {
  title: string;
  genreLabel: string;
  durationLabel: string;
  keywords: string[];
  creativity: number;
  logline: string;
  hook: string;
  tone: string;
  characters: GeneratedCharacter[];
  episodeBeats: EpisodeBeat[];
  scriptScenes: ScriptScene[];
  nextEpisodeHook: string;
  fullScriptText: string;
  generatedAt: string;
}

const PROJECT_STORAGE_KEY = 'latestDramaProject';
const AI_CONFIG_STORAGE_KEY = 'dramaAIProviderConfig';

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const defaultAIProviderConfig: AIProviderConfig = {
  apiKey: '',
  baseUrl: 'https://api.openai.com/v1',
  model: 'gpt-4.1-mini',
};

export const getStoredAIProviderConfig = (): AIProviderConfig => {
  if (!canUseStorage()) {
    return defaultAIProviderConfig;
  }

  const rawValue = window.localStorage.getItem(AI_CONFIG_STORAGE_KEY);

  if (!rawValue) {
    return defaultAIProviderConfig;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<AIProviderConfig>;
    return {
      apiKey: parsedValue.apiKey || '',
      baseUrl: parsedValue.baseUrl || defaultAIProviderConfig.baseUrl,
      model: parsedValue.model || defaultAIProviderConfig.model,
    };
  } catch {
    return defaultAIProviderConfig;
  }
};

export const saveAIProviderConfig = (config: AIProviderConfig) => {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(AI_CONFIG_STORAGE_KEY, JSON.stringify(config));
};

export const getLatestDramaProject = (): GeneratedDramaProject | null => {
  if (!canUseStorage()) {
    return null;
  }

  const rawValue = window.localStorage.getItem(PROJECT_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<GeneratedDramaProject>;
    if (
      typeof parsedValue?.title !== 'string' ||
      !Array.isArray(parsedValue?.characters) ||
      !Array.isArray(parsedValue?.episodeBeats) ||
      !Array.isArray(parsedValue?.scriptScenes)
    ) {
      return null;
    }

    return parsedValue as GeneratedDramaProject;
  } catch {
    return null;
  }
};

export const saveLatestDramaProject = (project: GeneratedDramaProject) => {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify(project));
};

export const formatDramaProjectForExport = (project: GeneratedDramaProject): string => {
  const characterBlock = project.characters
    .map((character) => {
      const relationshipText = character.relationships.length > 0 ? character.relationships.join('、') : '暂无';
      return [
        `- ${character.name}｜${character.age}｜${character.occupation}`,
        `  性格：${character.personality}`,
        `  背景：${character.background}`,
        `  外貌：${character.appearance}`,
        `  关系：${relationshipText}`,
      ].join('\n');
    })
    .join('\n');

  const episodeBlock = project.episodeBeats
    .map((beat) => `- 第${beat.episode}集《${beat.title}》：${beat.summary}｜吊点：${beat.cliffhanger}`)
    .join('\n');

  const sceneBlock = project.scriptScenes
    .map((scene, index) => {
      return [
        `## 场景${index + 1}：${scene.title}`,
        `地点：${scene.location}`,
        `时长：${scene.duration}`,
        `镜头：${scene.shotType} / ${scene.cameraMovement}`,
        `人物：${scene.characters.join('、') || '待补充'}`,
        `描述：${scene.description}`,
        `对白：${scene.dialogue}`,
        `灯光：${scene.lighting}`,
        `声音：${scene.sound}`,
        `备注：${scene.notes}`,
      ].join('\n');
    })
    .join('\n\n');

  return [
    `# ${project.title}`,
    '',
    `一句话卖点：${project.logline}`,
    `开场钩子：${project.hook}`,
    `风格基调：${project.tone}`,
    `类型：${project.genreLabel}`,
    `时长：${project.durationLabel}`,
    `关键词：${project.keywords.join('、')}`,
    '',
    '## 人物关系',
    characterBlock,
    '',
    '## 三集推进',
    episodeBlock,
    '',
    '## 第1集完整稿',
    project.fullScriptText,
    '',
    '## 分镜草稿',
    sceneBlock,
    '',
    `## 下一集吊点\n${project.nextEpisodeHook}`,
  ].join('\n');
};
